import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  PackageIcon,
  ShieldCheckIcon,
  PlugsIcon,
  // TimerIcon,
  // XCircleIcon,
  CaretLeftIcon,
  SignOutIcon,
} from "@phosphor-icons/react";
import { PricingSkeleton } from "../../components/SkeletonLoaders";
import {
  useStripePlans,
  useInitiateSubscription,
  useSubscriptionDetails,
  useActivateTrial,
} from "../../hooks";
import { useAuth } from "../../hooks";
import AnimatedModal from "../../components/AnimatedModal";
import PricingCard from "./components/PricingCard";
import { getPlanMeta } from "./interface/pricingMeta";
import { cn } from "../../lib/utils";

const TRUST_ITEMS = [
  {
    key: "trust_no_sdk",
    fallback: "Zero SDK · OAuth 2.0 only",
    Icon: ShieldCheckIcon,
  },
  {
    key: "trust_connect",
    fallback: "Connect in under 5 minutes",
    Icon: PlugsIcon,
  },
  // {
  //   key: "trust_trial",
  //   fallback: "7-day trial · charged on Day 8",
  //   Icon: TimerIcon,
  // },
  // {
  //   key: "trust_cancel",
  //   fallback: "Cancel anytime before Day 8",
  //   Icon: XCircleIcon,
  // },
] as const;

const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isAnnual, setIsAnnual] = useState(false);

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const hasActiveSubscription =
    user?.subscription_status === "active" ||
    user?.subscription_status === "trialing";

  const { data: plansData, isLoading: isLoadingPlans } = useStripePlans();
  const { data: subDetails, isLoading: isLoadingDetails } =
    useSubscriptionDetails();
  const initiateMutation = useInitiateSubscription();
  const activateTrialMutation = useActivateTrial();
  const [trialError, setTrialError] = useState<string | null>(null);

  const handleStartTrial = () => {
    setTrialError(null);
    activateTrialMutation.mutate(undefined, {
      onSuccess: () => {
        localStorage.setItem("workspace_setup_pending", "true");
        window.location.href = "/workspace-setup";
      },
      onError: (err: Error) => {
        setTrialError(
          err?.message ||
          t("pricing.trialError", "Failed to activate trial. Please try again."),
        );
      },
    });
  };

  const plans = plansData?.results?.filter((p) => p.is_active) ?? [];
  const isLoading = isLoadingPlans || isLoadingDetails;
  const canStartTrial = !subDetails?.plan && !subDetails?.has_used_trial;

  const handleLogout = () => {
    logout();
  };

  const handleSubscribe = (planId: number) => {
    initiateMutation.mutate(
      {
        plan_id: planId,
        success_url: `${window.location.origin}/billing`,
        cancel_url: `${window.location.origin}/pricing`,
      },
      {
        onSuccess: (res) => {
          if (res.checkout_url) window.location.href = res.checkout_url;
        },
        onError: (err: Error) =>
          toast.error(
            err?.message ||
            t("pricing.initiateError", "Failed to initiate checkout"),
          ),
      },
    );
  };

  if (isLoading) return <PricingSkeleton />;

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[linear-gradient(160deg,#eef2fc_0%,#f5f7fd_40%,#edf1fb_70%,#f2f5fd_100%)]">
      {/* ── Atmospheric blobs ── */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(ellipse 800px 560px at -8% -8%,  rgba(26,61,124,0.12)  0%, transparent 52%),
            radial-gradient(ellipse 700px 560px at 108% 8%,  rgba(42,91,168,0.09)  0%, transparent 52%),
            radial-gradient(ellipse 560px 440px at 52% 110%, rgba(48,205,115,0.08) 0%, transparent 52%),
            radial-gradient(ellipse 340px 260px at 80% 52%,  rgba(26,61,124,0.05)  0%, transparent 50%)
          `,
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 h-[100dvh] pointer-events-none z-0 bg-[length:52px_52px]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(26,61,124,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,61,124,0.035) 1px, transparent 1px)
          `,
        }}
      />

      <div className="relative z-[1]">
        {/* ── Back button for already-subscribed users updating their plan ── */}
        {hasActiveSubscription ? (
          <div className="fixed top-4 left-5 z-10">
            <button
              onClick={() => navigate("/billing")}
              className="inline-flex items-center gap-[6px] px-4 py-2 rounded-lg border border-[rgba(26,61,124,0.2)] bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] text-[#1a3d7c] text-[13px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-white"
            >
              <CaretLeftIcon size={14} weight="bold" />
              {t("billing.backToBilling", "Back to Billing")}
            </button>
          </div>
        ) : (
          <div className="fixed top-4 right-5 z-10">
            <button
              onClick={() => setShowLogoutModal(true)}
              className="inline-flex items-center gap-[6px] px-4 py-2 rounded-lg border border-red-200 bg-[rgba(255,255,255,0.85)] backdrop-blur-[6px] text-red-600 text-[13px] font-semibold cursor-pointer transition-colors duration-150 hover:bg-red-50"
            >
              {t("common.logout", "Logout")}
              <SignOutIcon size={16} weight="bold" />
            </button>
          </div>
        )}

        <AnimatedModal
          isConfirmation
          show={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogout}
          title={t("common.confirm_logout")}
          children={
            <div className="text-gray-700 text-center">
              {t("common.logout_confirmation_message")}
            </div>
          }
          confirmText={t("common.logout")}
          confirmButtonColor="red"
        />

        {/* ══════════════ HERO ══════════════ */}
        <section className="animate-[p-fade-in_0.55s_cubic-bezier(0.4,0,0.2,1)_both] py-[52px] px-6 pb-[44px] text-center">
          <div className="max-w-[620px] mx-auto">
            {/* Pill badge */}
            <div className="flex justify-center mb-[22px]">
              <span className="inline-flex items-center gap-2 px-[18px] py-[6px] rounded-full text-[11px] font-bold tracking-[0.07em] uppercase bg-[rgba(255,255,255,0.85)] text-[#1a3d7c] border-[1.5px] border-[rgba(26,61,124,0.22)] shadow-[0_2px_12px_rgba(26,61,124,0.10)]">
                <span className="w-[7px] h-[7px] rounded-full bg-[#1a3d7c] shrink-0 animate-[p-ring_2.4s_ease-in-out_infinite]" />
                {t(
                  "pricing.hero_badge",
                  "Transparent pricing · Scale-based, never feature-gated",
                )}
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-sans text-[clamp(28px,5.2vw,52px)] font-black leading-[1.05] tracking-[-0.03em] mb-[18px]">
              <span className="text-[#0f172a]">
                {t("pricing.hero_h1_line1", "Your apps earn.")}
              </span>
              <br />
              <span className="inline-block bg-[linear-gradient(118deg,#1a3d7c_0%,#2a5ba8_50%,#30cd73_100%)] bg-clip-text text-transparent">
                {t("pricing.hero_h1_line2", "Know exactly how much.")}
              </span>
            </h1>

            <p className="text-[15px] text-[#475569] leading-[1.72] max-w-[480px] mx-auto mb-[30px] font-normal">
              {t(
                "pricing.hero_subtitle",
                "One dashboard. Every revenue stream. Every ad network. Your true profit margin — finally visible.",
              )}
            </p>

            {/* Billing toggle */}
            {plans.length > 0 && (
              <div className="flex flex-col items-center gap-[9px]">
                <div className="inline-flex items-center gap-[11px] bg-[rgba(255,255,255,0.90)] border-[1.5px] border-[rgba(26,61,124,0.18)] rounded-full px-[22px] py-2 shadow-[0_4px_18px_rgba(26,61,124,0.10)]">
                  <span
                    className={cn(
                      "text-[13.5px] font-bold transition-colors [transition-duration:220ms] select-none",
                      isAnnual ? "text-[#94a3b8]" : "text-[#0f172a]",
                    )}
                  >
                    {t("pricing.billing_monthly", "Monthly")}
                  </span>

                  <div
                    role="switch"
                    aria-checked={isAnnual}
                    onClick={() => setIsAnnual((v) => !v)}
                    className={cn(
                      "w-12 h-[27px] rounded-full border-[1.5px] cursor-pointer relative shrink-0 transition-[background,border-color] [transition-duration:260ms]",
                      isAnnual
                        ? "bg-[linear-gradient(135deg,#1a3d7c,#2a5ba8)] border-[#1a3d7c]"
                        : "bg-[rgba(26,61,124,0.18)] border-[rgba(26,61,124,0.35)]",
                    )}
                  >
                    <div
                      className={cn(
                        "w-[21px] h-[21px] bg-white rounded-full absolute top-[2px] left-[2px] transition-transform [transition-duration:280ms] cubic-bezier(0.4,0,0.2,1) shadow-[0_1px_6px_rgba(26,61,124,0.22)]",
                        isAnnual ? "translate-x-[21px]" : "translate-x-0",
                      )}
                    />
                  </div>

                  <span
                    className={cn(
                      "text-[13.5px] font-bold transition-colors [transition-duration:220ms] select-none",
                      isAnnual ? "text-[#0f172a]" : "text-[#94a3b8]",
                    )}
                  >
                    {t("pricing.billing_annual", "Annual")}
                  </span>

                  <span className="inline-flex items-center px-[10px] py-[3px] rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-[#f0fdf4] text-[#15803d] border border-[#86efac]">
                    {t("pricing.save_20", "Save 20%")}
                  </span>
                </div>

                <p className="text-[11.5px] text-[#64748b] font-normal">
                  {t(
                    "pricing.annual_switch_hint",
                    "Switch to annual billing and lock in a full year at 20% off",
                  )}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ══════════════ CARDS ══════════════ */}
        <div className="animate-[p-fadein_0.65s_0.10s_cubic-bezier(0.4,0,0.2,1)_both] px-5 pb-12 flex justify-center">
          {plans.length > 0 ? (
            <div
              className={cn(
                "grid gap-6 items-start justify-items-stretch w-full",
                plans.length === 1 && "max-w-[420px] grid-cols-1",
                plans.length === 2 &&
                "max-w-[860px] grid-cols-1 sm:grid-cols-2",
                plans.length === 3 &&
                "max-w-[1100px] grid-cols-1 md:grid-cols-3",
                plans.length >= 4 &&
                "max-w-[1320px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
              )}
            >
              {plans.map((plan, idx) => (
                <PricingCard
                  key={plan.id}
                  plan={plan}
                  meta={getPlanMeta(plan.name, idx)}
                  isAnnual={isAnnual}
                  canStartTrial={canStartTrial}
                  onSubscribe={handleSubscribe}
                  isPending={initiateMutation.isPending}
                  pendingPlanId={initiateMutation.variables?.plan_id ?? null}
                />
              ))}
            </div>
          ) : (
            <div className="relative w-full max-w-[560px] overflow-hidden rounded-[28px] p-[1.5px] shadow-[0_8px_48px_rgba(26,61,124,0.18)]"
              style={{ background: "linear-gradient(135deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)" }}
            >
              {/* inner card */}
              <div className="relative rounded-[27px] bg-[rgba(255,255,255,0.92)] backdrop-blur-[12px] px-10 py-14 flex flex-col items-center gap-5 overflow-hidden">

                {/* soft glow blobs inside card */}
                <div aria-hidden className="absolute -top-10 -left-10 w-48 h-48 rounded-full opacity-20 pointer-events-none"
                  style={{ background: "radial-gradient(circle,#2a5ba8,transparent 70%)" }} />
                <div aria-hidden className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full opacity-20 pointer-events-none"
                  style={{ background: "radial-gradient(circle,#30cd73,transparent 70%)" }} />

                {/* icon with gradient ring */}
                <div className="relative flex items-center justify-center w-[72px] h-[72px] rounded-full"
                  style={{ background: "linear-gradient(135deg,rgba(26,61,124,0.12),rgba(48,205,115,0.12))" }}
                >
                  <PackageIcon size={34} weight="duotone" color="#2a5ba8" className="block" />
                </div>

                {/* heading — same gradient as hero */}
                <div className="text-center">
                  <p className="text-[22px] font-black tracking-[-0.02em] leading-tight mb-[6px]"
                    style={{
                      background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 50%,#30cd73 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {t("pricing.comingSoon", "Pricing Coming Soon")}
                  </p>
                  {/* <p className="text-[#64748b] text-[13.5px] font-normal leading-relaxed">
                    {t("pricing.noPlans", "No active plans found at the moment.")}
                  </p> */}
                </div>

                {/* divider */}
                <div className="w-16 h-[2px] rounded-full"
                  style={{ background: "linear-gradient(90deg,#1a3d7c,#30cd73)" }} />

                {/* trial nudge text */}
                <p className="text-[12px] text-[#475569] font-medium text-center -mb-1">
                  {t("pricing.trialNudge", "Get full access — free for 21 days, no card required")}
                </p>

                {trialError && (
                  <p className="text-red-500 text-[13px] font-medium text-center px-2 -my-1">
                    {trialError}
                  </p>
                )}

                {/* CTA button — gradient fill */}
                <button
                  onClick={handleStartTrial}
                  disabled={activateTrialMutation.isPending}
                  className="relative inline-flex items-center gap-2 px-8 py-[13px] rounded-full text-white text-[14.5px] font-bold tracking-[0.01em] shadow-[0_4px_20px_rgba(26,61,124,0.30)] transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: "linear-gradient(118deg,#1a3d7c 0%,#2a5ba8 55%,#30cd73 100%)" }}
                >
                  {activateTrialMutation.isPending ? (
                    <>
                      <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      {t("pricing.activatingTrial", "Activating…")}
                    </>
                  ) : (
                    t("pricing.startTrial", "Start 21 Day Trial")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ══════════════ TRUST BAR ══════════════ */}
        <div className="animate-[p-fadein_0.55s_0.22s_cubic-bezier(0.4,0,0.2,1)_both] px-6 pb-[60px] flex justify-center">
          <div className={`flex gap-3 w-full max-w-[1000px] justify-center items-center`}>
            {TRUST_ITEMS.map(({ key, fallback, Icon }) => (
              <div
                key={key}
                className="flex items-center gap-[10px] px-4 py-[13px] bg-[rgba(255,255,255,0.75)] border border-[rgba(26,61,124,0.12)] rounded-[14px] shadow-[0_1px_8px_rgba(26,61,124,0.06)] backdrop-blur-[6px]"
              >
                <Icon
                  size={18}
                  color="#1a3d7c"
                  weight="duotone"
                  className="shrink-0"
                />
                <span className="text-[12px] font-medium text-[#1e293b] leading-[1.4]">
                  {t(`pricing.${key}`, fallback)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes p-ring {
          0%   { box-shadow: 0 0 0 0   rgba(26,61,124,0.55); }
          70%  { box-shadow: 0 0 0 9px rgba(26,61,124,0);    }
          100% { box-shadow: 0 0 0 0   rgba(26,61,124,0);    }
        }
        @keyframes p-fadein {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Pricing;
